import type { ApiError } from '@jmc/shared';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.fields = body?.error?.fields ?? {};
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * The single place the browser talks to the API.
 *
 * `X-Requested-With` is required by the server on every state-changing
 * request; together with the SameSite=Strict session cookie it is what stops
 * cross-site request forgery.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  const response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'jmc-web',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      parsed as ApiError | null,
      `Request failed (${response.status})`,
    );
  }

  return parsed as T;
}

/** Multipart upload, used by the import screen. */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'jmc-web' },
    body: formData,
  });

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      parsed as ApiError | null,
      `Upload failed (${response.status})`,
    );
  }
  return parsed as T;
}

/** Triggers a browser download for an authenticated endpoint. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'jmc-web' },
  });

  if (!response.ok) {
    throw new ApiRequestError(response.status, null, 'Could not download the file');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
